import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useConfig } from '../../../../../contexts/ConfigContext';
import { usePagePermission } from '../../../../../hooks/usePagePermission';
import { DuplicateCustomerError, type ExistingCustomerInfo } from '../../../../../lib/customerErrors';
import { formatLocalDate } from './helpers';

// ── Types ──────────────────────────────────────────────────────

type PermissionLevel = Parameters<ReturnType<typeof useAuth>['hasPermission']>[1];

/** Enriched booking row returned from booking-manage list operation */
export interface BookingRow {
  id: string;
  booking_code: string;
  booking_reference: string;
  simplybook_code: string;
  external_code?: string;
  source: string;
  booking_source?: string;
  status: string;
  customer_id?: string;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  service_date: string;
  service_time?: string;
  service_end_time?: string;
  start_time: string;
  end_time: string;
  booking_date_sast: string;
  territory_id: string | null;
  territory_name: string;
  territory_short_code: string | null;
  territory?: { name: string; short_code?: string };
  provider_name?: string;
  is_pos: boolean;
  email_bouncing?: boolean;
  email_last_bounce_at?: string | null;
  service_address?: string;
  suburb: string;
  city: string;
  notes: string;
  services: Array<{ name: string; quantity: number; [key: string]: unknown }>;
  service_name?: string;
  assigned_to?: string[];
  operator_names?: string[];
  webhook_payload?: Record<string, unknown>;
  // Voucher/collaboration/agent code attached to the booking (resolved from
  // voucher_redemptions). Lets operators see it before servicing so it carries
  // through to the service log and invoice.
  applied_code?: string;
  applied_code_type?: string;
  applied_code_status?: string;
  /** What the code is worth to the customer, e.g. "10% off". Null when it takes nothing off. */
  applied_code_discount?: string | null;
  [key: string]: unknown;
}

/**
 * The terms of the code attached to a booking, as booking-manage returns them. Raw
 * numbers, not words: the currency symbol is the portal's job, not the server's.
 */
export interface AppliedCodeTerms {
  code: string;
  type: string;
  status: string;
  /** False for a plain agent commission code, which never reduces the customer's price. */
  discounts_customer: boolean;
  discount_type: string | null;
  percentage_value: number | null;
  voucher_value: number | null;
  max_discount_amount: number | null;
}

/**
 * What the code takes off the customer's price, in words, so an operator knows before
 * arriving. Mirrors code-validate's wording: a percentage code says its percentage and
 * its cap if it has one, a fixed code says its amount, and a plain agent commission code
 * returns null because the customer pays full price. The money is still calculated at the
 * service log and the invoice; this only describes it.
 */
export function describeCodeDiscount(
  terms: AppliedCodeTerms | null | undefined,
  formatCurrency: (amount: number) => string
): string | null {
  if (!terms || !terms.discounts_customer) return null;
  if (terms.discount_type === 'percentage' && terms.percentage_value) {
    return terms.max_discount_amount
      ? `${terms.percentage_value}% off (max ${formatCurrency(terms.max_discount_amount)})`
      : `${terms.percentage_value}% off`;
  }
  if (terms.voucher_value) return `${formatCurrency(terms.voucher_value)} off`;
  return null;
}

/** Success modal state */
export interface SuccessModalState {
  type: string;
  message?: string;
  booking?: Record<string, unknown>;
}

/** Rebook customer prefill */
export interface RebookCustomerData {
  id: string | null;
  customer_name: string;
  email: string;
  phone: string;
  address: string;
  suburb?: string;
  city?: string;
  territory_id: string | null;
}

/** Calendar filter state */
interface CalendarFilterState {
  status: string;
  territory_id: string;
  assigned_to: string;
  search: string;
  context: 'all' | 'pos' | 'cmb';
  show_blocks: boolean;
  /** Follow-up reminders on the calendar (Gary, 3 Nov 2026), on unless switched off. */
  show_reminders: boolean;
}

/** Operator availability block with enriched name */
interface OperatorBlockRow {
  id: string;
  operator_id: string | null;
  reason: string;
  start_date: string;
  end_date: string;
  is_all_day?: boolean;
  start_time?: string | null;
  end_time?: string | null;
  users?: { id: string; first_name: string | null; last_name: string | null } | null;
  operator_name: string;
  is_global: boolean;
  [key: string]: unknown;
}

/** Operator availability pattern */
interface OperatorPatternRow {
  id: string;
  operator_id: string;
  days: string[];
  start_time: string;
  end_time: string;
  is_active: boolean;
  [key: string]: unknown;
}

/** Filter/select option */
interface SelectOption {
  value: string;
  label: string;
}

/** Block time data */
export interface BlockTimeData {
  operator_id?: string;
  reason: string;
  start_date: string;
  end_date: string;
  is_all_day: boolean;
  start_time?: string;
  end_time?: string;
}

/** Booking form save data */
export interface BookingFormData {
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  service_date?: string;
  start_time?: string;
  end_time?: string;
  territory_id?: string;
  services?: Array<{ name: string; quantity: number }>;
  notes?: string;
  [key: string]: unknown;
}

/** Cancellation data */
export interface CancellationData {
  reason: string;
  notes?: string;
  notify_customer?: boolean;
}

// ── Hook ───────────────────────────────────────────────────────

export function useBookingsData() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, supabase, isSuperAdmin } = useAuth();
  const { config, formatCurrency, formatDate } = useConfig();
  const timezone = config?.timezone || 'UTC';
  const { canAccess, isLoading: permissionLoading } = usePagePermission('bookings');

  // Action-level permissions
  const canCreate = hasPermission('bookings', 'create' as PermissionLevel);
  const canEdit = hasPermission('bookings', 'edit' as PermissionLevel);
  const canAssign = hasPermission('bookings', 'assign' as PermissionLevel);
  const canCancel = hasPermission('bookings', 'cancel' as PermissionLevel);
  const canReassignTerritory = hasPermission('bookings', 'reassign_territory' as PermissionLevel) || isSuperAdmin;

  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successModal, setSuccessModal] = useState<SuccessModalState | null>(null);
  const [operatorCount, setOperatorCount] = useState<number | null>(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  // Modal state
  const [viewingBooking, setViewingBooking] = useState<BookingRow | null>(null);
  const [editingBooking, setEditingBooking] = useState<BookingRow | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [assigningBooking, setAssigningBooking] = useState<BookingRow | null>(null);
  const [cancellingBooking, setCancellingBooking] = useState<BookingRow | null>(null);
  const [reassigningTerritory, setReassigningTerritory] = useState<BookingRow | null>(null);

  // Calendar filter state
  const [calendarFilters, setCalendarFilters] = useState<CalendarFilterState>({
    status: 'all',
    territory_id: 'all',
    assigned_to: 'all',
    search: '',
    context: 'all',
    show_blocks: true,
    show_reminders: true
  });
  const [operatorBlocks, setOperatorBlocks] = useState<OperatorBlockRow[]>([]);
  const [calendarFiltersExpanded, setCalendarFiltersExpanded] = useState(false);
  // Operators see their blocked time on the calendar by default. Admins viewing
  // a whole team can turn it off when many operators' blocks get noisy.
  const showBlocks = calendarFilters.show_blocks !== false;
  const [operatorPatterns, setOperatorPatterns] = useState<OperatorPatternRow[]>([]);

  // State for rebook flow
  const [rebookCustomer, setRebookCustomer] = useState<RebookCustomerData | null>(null);

  // Handle resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadBookings();
    loadOperatorBlocks();
    loadOperatorPatterns();
    if (canAssign) {
      loadOperatorCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAssign]);

  // Auto-open create modal if ?create=true in URL
  useEffect(() => {
    if (searchParams.get('create') === 'true' && canCreate && !loading) {
      setEditingBooking(null);
      setShowFormModal(true);
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canCreate, loading]);

  // Handle ?action=create&customer_id=XXX URL param (rebook from other pages)
  useEffect(() => {
    const action = searchParams.get('action');
    const customerId = searchParams.get('customer_id');
    if (action === 'create' && customerId && canCreate && !loading) {
      (async () => {
        try {
          const { data, error } = await supabase
            .rpc('get_customer_for_service_log', { p_customer_id: customerId });
          if (!error && data?.[0]) {
            const c = data[0] as Record<string, unknown>;
            const billingAddress = c.billing_address as Record<string, unknown> | null;
            setRebookCustomer({
              id: c.id as string,
              customer_name: c.customer_name as string,
              email: (c.email as string) || '',
              phone: (c.phone as string) || '',
              address: (billingAddress?.address as string) || '',
              suburb: (billingAddress?.suburb as string) || '',
              city: (billingAddress?.city as string) || '',
              territory_id: (c.territory_id as string) || null,
            });
            setEditingBooking(null);
            setShowFormModal(true);
          }
        } catch (err) {
          console.error('Error fetching customer for rebook:', err);
        }
        searchParams.delete('action');
        searchParams.delete('customer_id');
        setSearchParams(searchParams, { replace: true });
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canCreate, loading, supabase]);

  // Auto-open view modal if ?view=BOOKING_ID in URL
  useEffect(() => {
    const bookingId = searchParams.get('view');
    if (bookingId && !loading && bookings.length > 0) {
      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        setViewingBooking(booking);
        searchParams.delete('view');
        setSearchParams(searchParams, { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, bookings]);

  // ── Data Loading ──────────────────────────────────────────────

  async function loadOperatorCount() {
    try {
      const { data, error } = await supabase.functions.invoke('booking-assign', {
        body: { operation: 'list_operators' }
      });
      if (!error && data?.operators) {
        setOperatorCount((data.operators as unknown[]).length);
      } else if (error) {
        setOperatorCount(2);
      }
    } catch (err) {
      console.error('Error loading operator count:', err);
      setOperatorCount(2);
    }
  }

  async function loadOperatorBlocks() {
    try {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 90);

      const startDate = formatLocalDate(today, timezone);
      const endDate = formatLocalDate(futureDate, timezone);

      const { data: blocks, error } = await supabase
        .from('availability_blocks')
        .select(`
          id,
          operator_id,
          reason,
          start_date,
          end_date,
          is_all_day,
          start_time,
          end_time,
          users!operator_id(id, first_name, last_name)
        `)
        .lte('start_date', endDate)
        .gte('end_date', startDate);

      if (error) {
        console.error('[Blocks] Error loading blocks:', error);
        return;
      }

      const blocksWithNames: OperatorBlockRow[] = ((blocks || []) as unknown as Array<{
        id: string;
        operator_id: string | null;
        reason: string;
        start_date: string;
        end_date: string;
        is_all_day: boolean;
        start_time: string | null;
        end_time: string | null;
        users: { id: string; first_name: string | null; last_name: string | null } | null;
      }>).map((b) => ({
        ...b,
        operator_name: b.users
          ? `${b.users.first_name || ''} ${b.users.last_name || ''}`.trim()
          : 'All Operators',
        is_global: !b.operator_id
      }));

      setOperatorBlocks(blocksWithNames);
    } catch (err) {
      console.error('Error loading operator blocks:', err);
    }
  }

  async function loadOperatorPatterns() {
    try {
      const { data: patterns, error } = await supabase
        .from('availability_patterns')
        .select('id, operator_id, days, start_time, end_time, is_active')
        .eq('is_active', true);

      if (error) {
        console.error('[Patterns] Error loading patterns:', error);
        return;
      }

      setOperatorPatterns((patterns || []) as OperatorPatternRow[]);
    } catch (err) {
      console.error('Error loading operator patterns:', err);
    }
  }

  async function loadBookings() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fnError } = await supabase.functions.invoke('booking-manage', {
        body: { operation: 'list' }
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const rawBookings = (data?.bookings || []) as Array<Record<string, unknown>>;

      const bookingsList: BookingRow[] = rawBookings.map((b) => {
        const territory = b.territory as { name: string; short_code?: string } | undefined;
        const rawServices = b.services as Array<{ name: string; quantity: number }> | undefined;

        return {
          ...b,
          id: b.id as string,
          booking_code: b.booking_code as string,
          booking_reference: b.booking_code as string,
          simplybook_code: (b.external_code as string) || '',
          source: (b.booking_source as string) || (b.source as string) || 'phone',
          status: b.status as string,
          customer_name: (b.customer_name as string) || '',
          service_date: (b.service_date as string) || '',
          territory_id: (b.territory_id as string) || null,
          territory_name: territory?.name || (b.provider_name as string) || 'Unknown',
          territory_short_code: territory?.short_code || null,
          is_pos: b.is_pos === true,
          service_address: (b.service_address as string) || (b.customer_address as string) || undefined,
          suburb: (b.suburb as string) || '',
          city: (b.city as string) || '',
          start_time: (b.service_time as string) || '',
          end_time: (b.service_end_time as string) || '',
          booking_date_sast: (b.service_date as string) || '',
          notes: (b.notes as string) || '',
          services: (rawServices && rawServices.length > 0) ? rawServices :
            (b.service_name ? [{ name: b.service_name as string, quantity: 1 }] : []),
          applied_code_discount: describeCodeDiscount(
            b.applied_code_terms as AppliedCodeTerms | null,
            formatCurrency
          ),
        } as BookingRow;
      });

      setBookings(bookingsList);
    } catch (err) {
      console.error('Error loading bookings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }

  // ── Memoized Options ──────────────────────────────────────────

  const operatorOptions = useMemo<SelectOption[]>(() => {
    const operatorMap = new Map<string, string>();
    bookings.forEach(b => {
      if (b.assigned_to && b.operator_names) {
        b.assigned_to.forEach((userId: string, idx: number) => {
          if (!operatorMap.has(userId)) {
            operatorMap.set(userId, b.operator_names![idx] || 'Unknown');
          }
        });
      }
    });
    // Also include operators from availability blocks (they may have no bookings yet)
    operatorBlocks.forEach(block => {
      if (block.operator_id && block.operator_name && !operatorMap.has(block.operator_id)) {
        operatorMap.set(block.operator_id, block.operator_name);
      }
    });
    return Array.from(operatorMap.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [bookings, operatorBlocks]);

  // Auto-select operator when the team genuinely has only one person — ensures
  // blocks show by default for solo-operator countries. Must wait until bookings
  // have loaded and count operators from bookings only (NOT from availability
  // blocks) — otherwise a country where one operator has blocks but multiple
  // operators have bookings ends up filtered to the block-owner on first render.
  useEffect(() => {
    if (loading) return;
    const bookingOperatorIds = new Set<string>();
    bookings.forEach(b => {
      if (Array.isArray(b.assigned_to)) {
        b.assigned_to.forEach(id => { if (id) bookingOperatorIds.add(id); });
      }
    });
    if (bookingOperatorIds.size === 1 && calendarFilters.assigned_to === 'all') {
      const onlyId = bookingOperatorIds.values().next().value as string;
      setCalendarFilters(prev => ({ ...prev, assigned_to: onlyId }));
    }
  }, [loading, bookings, calendarFilters.assigned_to]);

  const territoryOptions = useMemo<SelectOption[]>(() => {
    const territoryMap = new Map<string, string>();
    bookings.forEach(b => {
      if (b.territory_id && b.territory_name) {
        if (!territoryMap.has(b.territory_id)) {
          const displayName = b.territory_name.replace('CleanMyBed ', '').replace(' - ', ' ').trim();
          territoryMap.set(b.territory_id, displayName);
        }
      }
    });
    return Array.from(territoryMap.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [bookings]);

  const statusOptions = useMemo<SelectOption[]>(() => [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ], []);

  const contextOptions = useMemo<SelectOption[]>(() => [
    { value: 'pos', label: 'POS' },
    { value: 'cmb', label: 'CMB' }
  ], []);

  const sourceOptions = useMemo<SelectOption[]>(() => [
    { value: 'simplybook', label: 'SimplyBook (Legacy)' },
    { value: 'native_widget', label: 'Native Widget' },
    { value: 'portal', label: 'Portal' },
    { value: 'phone', label: 'Phone' },
    { value: 'converted', label: 'Converted' }
  ], []);

  // Filtered bookings for calendar view
  const filteredCalendarBookings = useMemo(() => {
    const searchLower = calendarFilters.search?.toLowerCase().trim() || '';

    return bookings.filter(b => {
      if (searchLower) {
        const searchFields = [
          b.customer_name, b.booking_reference, b.booking_code,
          b.simplybook_code, b.customer_email, b.customer_phone,
          b.service_address, b.operator_names?.join(' ')
        ].filter(Boolean).join(' ').toLowerCase();

        if (!searchFields.includes(searchLower)) return false;
      }
      // Status filter — 'all' shows everything, otherwise exact match.
      // 'confirmed' also matches the legacy aliases ('active', 'approved')
      // so any pre-backfill row from another country still resolves correctly.
      if (calendarFilters.status === 'confirmed') {
        const confirmedAliases = ['confirmed', 'active', 'approved'];
        if (!confirmedAliases.includes(b.status)) return false;
      } else if (calendarFilters.status === 'cancelled') {
        const cancelledAliases = ['cancelled', 'canceled'];
        if (!cancelledAliases.includes(b.status)) return false;
      } else if (calendarFilters.status !== 'all' && b.status !== calendarFilters.status) {
        return false;
      }
      if (calendarFilters.territory_id !== 'all' && b.territory_id !== calendarFilters.territory_id) return false;
      if (calendarFilters.assigned_to !== 'all' && !b.assigned_to?.includes(calendarFilters.assigned_to)) return false;
      if (calendarFilters.context === 'pos' && !b.is_pos) return false;
      if (calendarFilters.context === 'cmb' && b.is_pos) return false;
      return true;
    });
  }, [bookings, calendarFilters]);

  // Count active calendar filters
  const activeCalendarFilterCount = [
    calendarFilters.status !== 'all',
    calendarFilters.territory_id !== 'all',
    calendarFilters.assigned_to !== 'all',
    calendarFilters.context !== 'all'
  ].filter(Boolean).length;

  // ── Event Handlers ────────────────────────────────────────────

  function handleViewBooking(row: BookingRow) {
    setViewingBooking(row);
  }

  function handleEditBooking(row: BookingRow) {
    setEditingBooking(row);
    setShowFormModal(true);
  }

  function handleNewBooking() {
    setEditingBooking(null);
    setShowFormModal(true);
  }

  function handleBlockTime() {
    setShowBlockModal(true);
  }

  async function handleSaveBlock(blockData: Record<string, unknown>) {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('booking-availability', {
        body: { operation: 'create_block', ...blockData }
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      setShowBlockModal(false);
      setSuccessModal({ type: 'blocked', message: 'Time blocked successfully' });
      setTimeout(() => setSuccessModal(null), 4000);
    } catch (err) {
      console.error('Error saving block:', err);
      setError('Failed to block time: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  }

  function handleCloneBooking(sbBooking: BookingRow) {
    const clonedBooking = {
      customer_name: sbBooking.customer_name,
      customer_email: sbBooking.customer_email,
      customer_phone: sbBooking.customer_phone,
      customer_address: sbBooking.service_address || sbBooking.customer_address,
      service_date: sbBooking.booking_date_sast,
      start_time: sbBooking.start_time,
      end_time: sbBooking.end_time,
      territory_id: sbBooking.territory_id,
      services: sbBooking.services || [],
      notes: `Cloned from SimplyBook booking ${sbBooking.booking_reference || sbBooking.booking_code}${sbBooking.notes ? '\n\nOriginal notes: ' + sbBooking.notes : ''}`,
      source: 'converted',
      _clonedFrom: sbBooking.id,
      _clonedFromRef: sbBooking.booking_reference || sbBooking.booking_code
    } as unknown as BookingRow;
    setEditingBooking(clonedBooking);
    setShowFormModal(true);
  }

  function handleRebook(booking: BookingRow) {
    setRebookCustomer({
      id: booking.customer_id || null,
      customer_name: booking.customer_name || '',
      email: booking.customer_email || '',
      phone: booking.customer_phone || '',
      address: booking.service_address || booking.customer_address || '',
      territory_id: booking.territory_id || null,
    });
    setEditingBooking(null);
    setShowFormModal(true);
  }

  function handleCloseFormModal() {
    setShowFormModal(false);
    setEditingBooking(null);
    setRebookCustomer(null);
    loadBookings();
  }

  async function handleSaveBooking(bookingData: BookingFormData) {
    try {
      const isUpdate = editingBooking?.id && !(editingBooking as Record<string, unknown>)?._clonedFrom;
      const operation = isUpdate ? 'update' : 'create';

      const { data, error: fnError } = await supabase.functions.invoke('booking-manage', {
        body: {
          operation,
          id: isUpdate ? editingBooking!.id : undefined,
          ...bookingData
        }
      });

      if (fnError) {
        let message = fnError.message;
        try {
          const body = await (fnError as Record<string, unknown>).context as Record<string, unknown> | undefined;
          const jsonBody = body ? await (body as { json: () => Promise<Record<string, unknown>> }).json() : null;
          // The customer email already belongs to someone. Surface the existing
          // record so the wizard can offer to link to it instead of erroring.
          if (jsonBody?.code === 'duplicate_customer_email' && jsonBody.existing_customer) {
            throw new DuplicateCustomerError(
              (jsonBody.error as string) || 'A customer with this email already exists.',
              jsonBody.existing_customer as ExistingCustomerInfo,
            );
          }
          if (jsonBody?.error) message = jsonBody.error as string;
        } catch (parseErr) {
          if (parseErr instanceof DuplicateCustomerError) throw parseErr;
          /* otherwise fall through to the default message */
        }
        throw new Error(message);
      }
      if (data?.error) throw new Error(data.error);

      if (isUpdate) {
        await loadBookings();
        handleCloseFormModal();
        const successBooking = {
          ...editingBooking,
          ...bookingData,
          booking_code: editingBooking!.booking_reference || editingBooking!.booking_code
        };
        setSuccessModal({ type: 'updated', booking: successBooking as Record<string, unknown> });
      }
      return data?.booking;
    } catch (err) {
      // The wizard handles the duplicate-customer case with its own confirmation
      // dialog, so don't also raise the page-level error banner for it.
      if (err instanceof DuplicateCustomerError) throw err;
      console.error('Error saving booking:', err);
      setError('Failed to save booking: ' + (err instanceof Error ? err.message : 'Unknown error'));
      throw err;
    }
  }

  function handleAssign(row: BookingRow) {
    setAssigningBooking(row);
  }

  async function handleSaveAssignment(_data: Record<string, unknown>) {
    await loadBookings();
    setAssigningBooking(null);
    setSuccessModal({ type: 'assigned', message: 'Operator assigned successfully' });
    setTimeout(() => setSuccessModal(null), 4000);
  }

  function handleCancel(row: BookingRow) {
    setCancellingBooking(row);
  }

  function handleReassignTerritory(row: BookingRow) {
    setReassigningTerritory(row);
  }

  async function handleSaveReassignTerritory(_data: Record<string, unknown>) {
    await loadBookings();
    setReassigningTerritory(null);
  }

  async function handleSaveCancellation(cancellationData: CancellationData) {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('booking-manage', {
        body: {
          operation: 'cancel',
          id: cancellingBooking!.id,
          cancellation_reason: cancellationData.reason,
          cancellation_notes: cancellationData.notes,
          skip_notification: cancellationData.notify_customer === false,
        }
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      await loadBookings();
      setCancellingBooking(null);
    } catch (err) {
      console.error('Error cancelling booking:', err);
      setError('Failed to cancel booking: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setCancellingBooking(null);
    }
  }

  function handleExportCSV() {
    const headers = ['Booking Code', 'Customer', 'Email', 'Phone', 'Date', 'Time', 'Status', 'Territory', 'Operators', 'Source'];
    const rows = bookings.map(b => [
      b.booking_code || '',
      b.customer_name || '',
      b.customer_email || '',
      b.customer_phone || '',
      b.service_date || '',
      b.service_time || '',
      b.status || '',
      b.territory?.name || b.territory_name || '',
      (b.operator_names || []).join('; '),
      b.source || '',
    ]);

    const escapeCSV = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const csv = [headers.join(','), ...rows.map(r => r.map(escapeCSV).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-export-${formatLocalDate(new Date(), config?.timezone)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return {
    // Auth/config
    supabase, formatCurrency, formatDate, timezone,
    canAccess, permissionLoading,
    canCreate, canEdit, canAssign, canCancel, canReassignTerritory,
    // Data
    bookings, loading, error, setError, successModal, setSuccessModal,
    operatorCount, isMobile,
    // Modal state
    viewingBooking, setViewingBooking,
    editingBooking, setEditingBooking,
    showFormModal, setShowFormModal,
    showBlockModal, setShowBlockModal,
    assigningBooking, setAssigningBooking,
    cancellingBooking, setCancellingBooking,
    reassigningTerritory, setReassigningTerritory,
    rebookCustomer,
    // Calendar state
    calendarFilters, setCalendarFilters,
    calendarFiltersExpanded, setCalendarFiltersExpanded,
    activeCalendarFilterCount,
    showBlocks, operatorBlocks, operatorPatterns,
    filteredCalendarBookings,
    // Options
    statusOptions, operatorOptions, territoryOptions, contextOptions, sourceOptions,
    // Handlers
    loadBookings,
    handleViewBooking, handleEditBooking, handleNewBooking,
    handleBlockTime, handleSaveBlock,
    handleCloneBooking, handleRebook,
    handleCloseFormModal, handleSaveBooking,
    handleAssign, handleSaveAssignment,
    handleCancel, handleSaveCancellation,
    handleReassignTerritory, handleSaveReassignTerritory,
    handleExportCSV,
  };
}
